import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { differenceInYears, parseISO } from 'date-fns';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { useAuth } from '@/contexts/AuthContext';
import { PROFILE_TYPES, ROUTES } from '@/lib/constants';
import { AUTH } from '@/constants/testIds';
import { sanitizeInternalNext } from '@/lib/safeNext';
import { fetchClubs } from '@/lib/api';

const INITIAL = {
  first_name: '',
  first_surname: '',
  second_surname: '',
  email: '',
  password: '',
  password_confirmation: '',
  date_of_birth: '',
  profile_type: '',
  favorite_club_id: '',
  reference_club_id: '',
  privacy_policy_accepted: false,
  terms_accepted: false,
  marketing_consent: false,
  parent_guardian_name: '',
  parent_guardian_email: '',
  guardian_consent_accepted: false,
};

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Preserva el `?next=` saneado para que el flujo
  //   Registro -> Login -> contenido original  no se pierda.
  const safeNext = useMemo(
    () => sanitizeInternalNext(new URLSearchParams(location.search).get('next')),
    [location.search],
  );
  const loginHref = safeNext
    ? `${ROUTES.login}?next=${encodeURIComponent(safeNext)}`
    : ROUTES.login;
  const [form, setForm] = useState(INITIAL);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setClubsLoading(true);
      try {
        // Cargar clubes activos y no borrados para el selector de club de referencia.
        const data = await fetchClubs({ status: 'active', page_size: 60 });
        if (!cancelled) setClubs((data && data.items) || []);
      } catch {
        if (!cancelled) setClubs([]);
      } finally {
        if (!cancelled) setClubsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const age = useMemo(() => {
    if (!form.date_of_birth) return null;
    try {
      return differenceInYears(new Date(), parseISO(form.date_of_birth));
    } catch {
      return null;
    }
  }, [form.date_of_birth]);

  const isMinorPlayer = form.profile_type === 'player' && age !== null && age < 18;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSuccess(null);

    if (!form.first_name.trim() || !form.first_surname.trim()) {
      setError('Debes indicar tu nombre y al menos el primer apellido.');
      return;
    }
    if (form.password !== form.password_confirmation) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (!form.privacy_policy_accepted || !form.terms_accepted) {
      setError('Debes aceptar la política de privacidad y los términos.');
      return;
    }
    if (!form.profile_type) {
      setError('Selecciona un tipo de perfil.');
      return;
    }
    if (isMinorPlayer) {
      if (!form.parent_guardian_name || !form.parent_guardian_email || !form.guardian_consent_accepted) {
        setError('Como jugador menor de 18 años, debes aportar los datos del tutor legal y su consentimiento.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const fs = form.first_surname.trim();
      const ss = form.second_surname.trim();
      const payload = {
        first_name: form.first_name.trim(),
        first_surname: fs,
        second_surname: ss || null,
        // Legacy: se envía la concat sólo para retrocompat con validadores
        // antiguos; el backend lo recompone igualmente.
        last_name: [fs, ss].filter(Boolean).join(' '),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        password_confirmation: form.password_confirmation,
        date_of_birth: form.date_of_birth,
        profile_type: form.profile_type,
        favorite_club_id: form.favorite_club_id || null,
        reference_club_id: form.reference_club_id || null,
        privacy_policy_accepted: form.privacy_policy_accepted,
        terms_accepted: form.terms_accepted,
        marketing_consent: form.marketing_consent,
        parent_guardian_name: isMinorPlayer ? form.parent_guardian_name.trim() : null,
        parent_guardian_email: isMinorPlayer ? form.parent_guardian_email.trim().toLowerCase() : null,
        guardian_consent_accepted: isMinorPlayer ? form.guardian_consent_accepted : null,
      };
      const data = await register(payload);
      if (data?.account_status === 'pending_guardian_consent') {
        setSuccess(
          'Registro recibido. Tu cuenta queda pendiente del consentimiento del tutor legal. Recibirás indicaciones en cuanto se valide.'
        );
      } else {
        setSuccess('Cuenta creada correctamente. Ya puedes acceder con tu email y contraseña.');
        setTimeout(() => navigate(loginHref), 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Crea tu cuenta"
      subtitle="Únete a NetScouters y forma parte del ecosistema deportivo de clubes, jugadores y talento."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        {/* Datos básicos */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="first_name" className="text-sm font-medium">Nombre</Label>
            <Input
              id="first_name"
              required
              value={form.first_name}
              onChange={(e) => setField('first_name', e.target.value)}
              data-testid={AUTH.registerFirstName}
              className="h-11 bg-white"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="first_surname" className="text-sm font-medium">Primer apellido</Label>
            <Input
              id="first_surname"
              required
              value={form.first_surname}
              onChange={(e) => setField('first_surname', e.target.value)}
              data-testid="register-first-surname"
              className="h-11 bg-white"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="second_surname" className="text-sm font-medium">Segundo apellido</Label>
            <Input
              id="second_surname"
              value={form.second_surname}
              onChange={(e) => setField('second_surname', e.target.value)}
              data-testid="register-second-surname"
              className="h-11 bg-white"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            data-testid={AUTH.registerEmail}
            className="h-11 bg-white"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPwd ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={form.password}
                onChange={(e) => setField('password', e.target.value)}
                data-testid={AUTH.registerPassword}
                className="h-11 bg-white pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[var(--ns-text-muted)] hover:text-[var(--ns-text)]"
                aria-label={showPwd ? 'Ocultar' : 'Mostrar'}
                tabIndex={-1}
              >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[11px] text-[var(--ns-text-muted)] leading-snug">
              Mín. 8 caracteres, con mayúscula, minúscula y número.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password_confirmation" className="text-sm font-medium">
              Confirmar contraseña
            </Label>
            <Input
              id="password_confirmation"
              type={showPwd ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={form.password_confirmation}
              onChange={(e) => setField('password_confirmation', e.target.value)}
              data-testid={AUTH.registerPasswordConfirmation}
              className="h-11 bg-white"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dob" className="text-sm font-medium">Fecha de nacimiento</Label>
          <Input
            id="dob"
            type="date"
            required
            value={form.date_of_birth}
            onChange={(e) => setField('date_of_birth', e.target.value)}
            data-testid={AUTH.registerDob}
            className="h-11 bg-white"
          />
          {age !== null && (
            <p className="text-[11px] text-[var(--ns-text-muted)]">Edad calculada: {age} años</p>
          )}
        </div>

        {/* Profile type */}
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium">Tipo de perfil</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PROFILE_TYPES.map((p) => {
              const active = form.profile_type === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  data-testid={AUTH.registerProfileType(p.value)}
                  onClick={() => setField('profile_type', p.value)}
                  className={`text-left p-3 rounded-xl border transition-all duration-150 ${
                    active
                      ? 'border-[var(--ns-gold)] bg-[var(--ns-amber-50)] shadow-[var(--ns-shadow-soft)]'
                      : 'border-[var(--ns-border)] bg-white hover:border-[var(--ns-gold)]/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[var(--ns-text)] text-sm">{p.label}</span>
                    {active && <CheckCircle2 className="h-4 w-4 text-[var(--ns-gold-600)]" />}
                  </div>
                  <p className="text-[12px] text-[var(--ns-text-muted)] mt-0.5">{p.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Club de referencia (atribución económica) · obligatorio.
            Reemplaza visualmente al antiguo "Club favorito". `favorite_club_id`
            no se expone en el formulario para evitar confusión semántica. */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reference_club" className="text-sm font-medium">
            Club de referencia <span className="text-red-600">*</span>
          </Label>
          <select
            id="reference_club"
            required
            value={form.reference_club_id}
            onChange={(e) => setField('reference_club_id', e.target.value)}
            disabled={clubsLoading}
            data-testid="register-reference-club"
            className="h-11 w-full rounded-md border border-[var(--ns-border)] bg-white px-3 text-sm"
          >
            <option value="" disabled>
              {clubsLoading ? 'Cargando clubes…' : 'Selecciona tu club de referencia'}
            </option>
            {clubs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <p className="text-xs text-[var(--ns-text-muted)]">
            Selecciona el club al que quieres apoyar con tus compras en NetScouters.
          </p>
        </div>

        {/* Tutor (condicional) */}
        {isMinorPlayer && (
          <div className="p-4 rounded-xl border border-[var(--ns-gold)]/40 bg-[var(--ns-amber-50)]">
            <p className="text-sm font-semibold text-[var(--ns-gold-700)] mb-3">
              Datos del tutor legal (obligatorio para jugadores menores de 18)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="guardian_name" className="text-sm font-medium">Nombre del tutor</Label>
                <Input
                  id="guardian_name"
                  value={form.parent_guardian_name}
                  onChange={(e) => setField('parent_guardian_name', e.target.value)}
                  data-testid={AUTH.registerGuardianName}
                  className="h-11 bg-white"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="guardian_email" className="text-sm font-medium">Email del tutor</Label>
                <Input
                  id="guardian_email"
                  type="email"
                  value={form.parent_guardian_email}
                  onChange={(e) => setField('parent_guardian_email', e.target.value)}
                  data-testid={AUTH.registerGuardianEmail}
                  className="h-11 bg-white"
                />
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-[var(--ns-text)] mt-3 cursor-pointer">
              <Checkbox
                checked={form.guardian_consent_accepted}
                onCheckedChange={(v) => setField('guardian_consent_accepted', !!v)}
                data-testid={AUTH.registerGuardianConsent}
                className="mt-0.5"
              />
              <span>
                Confirmo que soy el tutor/a legal o que cuento con su consentimiento expreso para registrar al menor.
              </span>
            </label>
          </div>
        )}

        {/* Consentimientos */}
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 text-sm text-[var(--ns-text)] cursor-pointer">
            <Checkbox
              checked={form.privacy_policy_accepted}
              onCheckedChange={(v) => setField('privacy_policy_accepted', !!v)}
              data-testid={AUTH.registerPrivacy}
              className="mt-0.5"
            />
            <span>
              He leído y acepto la{' '}
              <Link to={ROUTES.privacy} className="text-[var(--ns-gold-600)] hover:underline">
                política de privacidad
              </Link>
              .
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-[var(--ns-text)] cursor-pointer">
            <Checkbox
              checked={form.terms_accepted}
              onCheckedChange={(v) => setField('terms_accepted', !!v)}
              data-testid={AUTH.registerTerms}
              className="mt-0.5"
            />
            <span>
              Acepto los{' '}
              <Link to={ROUTES.legal} className="text-[var(--ns-gold-600)] hover:underline">
                términos y condiciones
              </Link>
              .
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-[var(--ns-text)] cursor-pointer">
            <Checkbox
              checked={form.marketing_consent}
              onCheckedChange={(v) => setField('marketing_consent', !!v)}
              data-testid={AUTH.registerMarketing}
              className="mt-0.5"
            />
            <span>
              Quiero recibir novedades, eventos y promociones (opcional).
            </span>
          </label>
        </div>

        {error && (
          <Alert variant="destructive" data-testid={AUTH.registerError}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert
            data-testid={AUTH.registerSuccess}
            className="border-[var(--ns-green)]/40 bg-[var(--ns-green)]/8"
          >
            <AlertDescription className="text-[var(--ns-green-700)]">
              {success}
            </AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid={AUTH.registerSubmit}
          className="h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0 shadow-[var(--ns-shadow-gold)] transition-all"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear cuenta'}
        </Button>

        <p className="text-sm text-[var(--ns-text-muted)] text-center">
          ¿Ya tienes cuenta?{' '}
          <Link
            to={loginHref}
            className="text-[var(--ns-gold-600)] hover:text-[var(--ns-gold-700)] font-semibold hover:underline"
            data-testid={AUTH.registerLoginLink}
          >
            Acceder
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
